import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #rewardUpdateTimer;
  #rewardToastTimer;
  #rewardRequestId = 0;
  #rewardToastQueue = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleCheckout, { capture: true });
    this.addEventListener('dialog:open', this.#watchCheckout);
    this.addEventListener('dialog:close', this.#stopWatchingCheckout);
    document.addEventListener(ThemeEvents.cartUpdate, this.#queueRewardUpdate);
    this.#primeRewardProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleCheckout, { capture: true });
    this.removeEventListener('dialog:open', this.#watchCheckout);
    this.removeEventListener('dialog:close', this.#stopWatchingCheckout);
    this.#stopWatchingCheckout();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#queueRewardUpdate);
    clearTimeout(this.#rewardUpdateTimer);
    clearTimeout(this.#rewardToastTimer);
  }

  #primeRewardProgress() {
    try {
      const reward = this.querySelector('[data-firstroot-rewards]');
      const subtotal = Number(reward?.dataset.initialSubtotal || 0);
      const total = Number(reward?.dataset.initialTotal || 0);
      this.#syncMilestoneBadges(subtotal);
      if (sessionStorage.getItem('firstroot-reward-subtotal') === null) sessionStorage.setItem('firstroot-reward-subtotal', String(subtotal));
      if (sessionStorage.getItem('firstroot-reward-total') === null) sessionStorage.setItem('firstroot-reward-total', String(total));
      if (sessionStorage.getItem('firstroot-reward-celebrated-stage') === null) {
        const initialStage = subtotal >= 99900 ? 3 : subtotal >= 49900 ? 2 : subtotal >= 19900 ? 1 : 0;
        sessionStorage.setItem('firstroot-reward-celebrated-stage', String(initialStage));
      }
      if (subtotal >= 99900) {
        const truck = this.querySelector('.firstroot-rewards__truck');
        if (truck) { truck.hidden = true; truck.style.opacity = '0'; }
      }
    } catch (_) {
      // Session storage can be unavailable in restricted browsing modes.
    }
  }
  #checkoutObserver;
  #checkoutCheckFrame = 0;

  // Some checkout apps intercept the click before it reaches the drawer.
  // Watch only while this drawer is open, and release its modal layer once
  // Razorpay's checkout is actually visible (not merely preloaded).
  #watchCheckout = () => {
    this.#stopWatchingCheckout();
    const check = () => {
      this.#checkoutCheckFrame = 0;
      if (!this.refs.dialog.open) return;
      const frame = document.querySelector('iframe.razorpay-checkout-frame');
      if (!frame) return;
      const bounds = frame.getBoundingClientRect();
      const style = getComputedStyle(frame);
      if (bounds.width > 0 && bounds.height > 0 && style.visibility !== 'hidden' && style.display !== 'none') {
        this.#stopWatchingCheckout();
        if (!this.refs.dialog.classList.contains('dialog-closing')) this.closeDialog();
      }
    };
    this.#checkoutObserver = new MutationObserver(() => {
      if (!this.#checkoutCheckFrame) this.#checkoutCheckFrame = requestAnimationFrame(check);
    });
    this.#checkoutObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
    check();
  };

  #stopWatchingCheckout = () => {
    this.#checkoutObserver?.disconnect();
    this.#checkoutObserver = undefined;
    cancelAnimationFrame(this.#checkoutCheckFrame);
    this.#checkoutCheckFrame = 0;
  };

  // Release the native modal's top layer before an external checkout takes focus.
  // Keep the original click/default action intact for Razorpay and Shopify checkout.
  #handleCheckout = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('button[name="checkout"], input[name="checkout"]')
      : null;
    if (!button || button.disabled || !this.refs.dialog.contains(button)) return;
    if (this.refs.dialog.open && !this.refs.dialog.classList.contains('dialog-closing')) {
      this.closeDialog();
    }
  };

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    if (!event.detail?.data?.didError) this.#queueRewardUpdate();
  };

  #queueRewardUpdate = () => {
    clearTimeout(this.#rewardUpdateTimer);
    const requestId = ++this.#rewardRequestId;
    this.#rewardUpdateTimer = setTimeout(() => {
      this.#syncRewardProgress(requestId).catch(() => {});
    }, 120);
  };

  #syncMilestoneBadges(amount) {
    const milestones = [
      { selector: '.firstroot-rewards__milestone--delivery', target: 19900, label: '✓' },
      { selector: '.firstroot-rewards__milestone--five', target: 49900, label: '✓' },
      { selector: '.firstroot-rewards__milestone--ten', target: 99900, label: '10%' },
    ];
    for (const milestone of milestones) {
      const badge = this.querySelector(milestone.selector);
      if (!badge) continue;
      const unlocked = amount >= milestone.target;
      badge.classList.toggle('is-unlocked', unlocked);
      const label = milestone.selector.includes('--five') && !unlocked ? '5%' : milestone.label;
      badge.querySelector('span')?.replaceChildren(document.createTextNode(unlocked ? milestone.label : label));
    }
  }

  #showMilestoneToast = async (label, stage) => {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    let toast = dialog.querySelector('[data-firstroot-reward-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'firstroot-reward-toast';
      toast.setAttribute('data-firstroot-reward-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
    }
    const desktopToast = window.matchMedia?.('(min-width: 750px)').matches;
    if (toast.hasAttribute('popover')) {
      try {
        if (typeof toast.hidePopover === 'function' && toast.matches(':popover-open')) toast.hidePopover();
      } catch (_) {}
      toast.removeAttribute('popover');
    }
    const toastHost = desktopToast ? (dialog.querySelector('.cart-drawer__inner') || dialog) : dialog;
    if (toast.parentElement !== toastHost) toastHost.append(toast);
    if (desktopToast) dialog.classList.add('has-reward-toast');
    toast.replaceChildren();
    const message = document.createElement('span');
    message.className = 'firstroot-reward-toast__text';
    message.textContent = '🎉 Congratulations! ' + label + ' unlocked! 🎉';
    toast.append(message);

    const confettiColors = ['#155b46', '#d6ad4d', '#8aa45d', '#ef8b68', '#f2d46d', '#ffffff'];
    const pieceCount = 22;
    for (let index = 0; index < pieceCount; index += 1) {
      const angle = (Math.PI * 2 * index) / pieceCount;
      const radius = 84 + (index % 4) * 17;
      const piece = document.createElement('i');
      piece.className = 'firstroot-reward-confetti' + (index % 3 === 0 ? ' is-round' : index % 3 === 1 ? ' is-ribbon' : '');
      piece.setAttribute('aria-hidden', 'true');
      piece.style.setProperty('--confetti-x', Math.cos(angle) * radius + 'px');
      piece.style.setProperty('--confetti-y', Math.sin(angle) * (radius * 0.72) + 'px');
      piece.style.setProperty('--confetti-spin', (360 + index * 91) + 'deg');
      piece.style.setProperty('--confetti-delay', (index % 5) * 18 + 'ms');
      piece.style.setProperty('--confetti-color', confettiColors[index % confettiColors.length]);
      toast.append(piece);
    }
    toast.dataset.stage = String(stage);
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    toast.classList.add('is-visible');
    await new Promise((resolve) => setTimeout(resolve, 2250));
    toast.classList.remove('is-visible');
    await new Promise((resolve) => setTimeout(resolve, 140));
    if (desktopToast) dialog.classList.remove('has-reward-toast');
  };

  #animateTruckTo = async (track, truck, targetPercent, badge, fill, startPercent) => {
    if (!track || !truck) return;
    const progressTo = Math.max(0, Math.min(100, Number(targetPercent) || 0));
    truck.hidden = false;
    truck.style.opacity = '1';

    const trackWidth = track.clientWidth;
    const trackRect = track.getBoundingClientRect();
    const trackStart = trackRect.left + track.clientLeft;
    const truckRect = truck.getBoundingClientRect();
    const fallbackProgress = Number.isFinite(Number(startPercent))
      ? Number(startPercent)
      : Number.parseFloat(truck.dataset.progressPercent || truck.style.left || '0');
    const visualProgress = trackWidth > 0
      ? ((truckRect.left + truckRect.width / 2 - trackStart) / trackWidth) * 100
      : fallbackProgress;
    const progressFrom = Math.max(0, Math.min(100, Number.isFinite(visualProgress) ? visualProgress : 0));
    const distance = trackWidth * Math.abs(progressTo - progressFrom) / 100;
    const desktopMotion = window.matchMedia?.('(min-width: 750px)').matches;
    const easing = desktopMotion ? 'cubic-bezier(.4, 0, .2, 1)' : 'cubic-bezier(.22, 1, .36, 1)';
    const duration = desktopMotion
      ? Math.max(560, Math.min(1050, distance * 2.2))
      : Math.max(350, Math.min(1500, distance * 3));
    track.style.setProperty('--firstroot-reward-motion-duration', duration + 'ms');

    truck.classList.remove('is-arrival-leaving');
    truck.style.setProperty('transform', 'translateX(-50%)', 'important');
    truck.style.setProperty(
      'transition',
      'left var(--firstroot-reward-motion-duration, 1050ms) ' + easing,
      'important',
    );
    if (fill) {
      fill.style.setProperty(
        'transition',
        'width var(--firstroot-reward-motion-duration, 1050ms) ' + easing,
        'important',
      );
    }

    if (distance < 0.5) {
      truck.style.left = progressTo + '%';
      if (fill) fill.style.width = progressTo + '%';
      truck.dataset.progressPercent = String(progressTo);
      return;
    }

    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(fallbackTimer);
        truck.removeEventListener('transitionend', onTransitionEnd);
        truck.removeEventListener('transitioncancel', finish);
        resolve();
      };
      const onTransitionEnd = (event) => {
        if (event.target === truck && event.propertyName === 'left') finish();
      };
      const fallbackTimer = setTimeout(finish, duration + 150);
      truck.addEventListener('transitionend', onTransitionEnd);
      truck.addEventListener('transitioncancel', finish);

      // Retarget from the current computed left position; never reset to the rail's start.
      requestAnimationFrame(() => {
        truck.style.left = progressTo + '%';
        if (fill) fill.style.width = progressTo + '%';
      });
    });

    truck.dataset.progressPercent = String(progressTo);
  };

  #syncRewardProgress = async (requestId) => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (requestId !== this.#rewardRequestId) return;
      const currentSubtotal = Number(cart.items_subtotal_price ?? cart.total_price ?? 0);
      const currentTotal = Number(cart.total_price ?? 0);
      const reward = this.querySelector('[data-firstroot-rewards]');
      const initialSubtotal = Number(reward?.dataset.initialSubtotal || 0);
      const initialTotal = Number(reward?.dataset.initialTotal || 0);
      const previousSubtotal = Number(sessionStorage.getItem('firstroot-reward-subtotal') ?? initialSubtotal);
      const previousTotal = Number(sessionStorage.getItem('firstroot-reward-total') ?? initialTotal);
      const stageFor = (amount) => amount >= 99900 ? 3 : amount >= 49900 ? 2 : amount >= 19900 ? 1 : 0;
      const previousStage = stageFor(previousSubtotal);
      const currentStage = stageFor(currentSubtotal);
      const changed = currentSubtotal !== previousSubtotal;
      const celebrationKey = 'firstroot-reward-celebrated-stage';
      let celebratedStage = Number(sessionStorage.getItem(celebrationKey) ?? previousStage);
      if (currentStage < previousStage) {
        celebratedStage = Math.min(celebratedStage, currentStage);
        sessionStorage.setItem(celebrationKey, String(celebratedStage));
      }
      const milestones = [
        { stage: 1, target: 19900, label: 'Free delivery', selector: '.firstroot-rewards__milestone--delivery' },
        { stage: 2, target: 49900, label: '5% off', selector: '.firstroot-rewards__milestone--five' },
        { stage: 3, target: 99900, label: '10% off', selector: '.firstroot-rewards__milestone--ten' },
      ];
      const newlyUnlocked = milestones.filter((milestone) => milestone.stage > celebratedStage && milestone.stage <= currentStage);

      if (changed || newlyUnlocked.length) {
        const track = this.querySelector('.firstroot-rewards__track');
        const fill = this.querySelector('.firstroot-rewards__fill');
        const truck = this.querySelector('.firstroot-rewards__truck');
        const pct = (value) => Math.max(0, Math.min(100, value / 99900 * 100));

        sessionStorage.setItem('firstroot-reward-subtotal', String(currentSubtotal));
        sessionStorage.setItem('firstroot-reward-total', String(currentTotal));

        if (truck && track && currentStage === 3 && previousStage === 3 && !newlyUnlocked.length) {
          truck.hidden = true;
          truck.style.opacity = '0';
          this.querySelector('.firstroot-rewards__milestone--ten')?.classList.remove('is-arriving');
        } else if (truck && track) {
          truck.hidden = false;
          truck.style.opacity = '1';
          if (newlyUnlocked.length) {
            let progressFrom = previousSubtotal;
            for (const milestone of newlyUnlocked) {
              const badge = this.querySelector(milestone.selector);
              badge?.classList.add('is-arriving');
              const targetPct = pct(milestone.target);
              await this.#animateTruckTo(track, truck, targetPct, badge, fill, pct(progressFrom));
              if (requestId !== this.#rewardRequestId) return;
              badge?.classList.remove('is-arriving');
              badge?.classList.add('is-unlocked', 'is-arrival-pop');
              if (milestone.stage === 2) badge?.querySelector('span')?.replaceChildren(document.createTextNode('✓'));

              this.#rewardToastQueue = this.#rewardToastQueue
                .then(() => this.#showMilestoneToast(milestone.label, milestone.stage))
                .catch(() => {});
              celebratedStage = milestone.stage;
              sessionStorage.setItem(celebrationKey, String(celebratedStage));
              progressFrom = milestone.target;

              if (milestone.stage === 3) {
                truck.classList.add('is-arrival-leaving');
                await new Promise((resolve) => setTimeout(resolve, 360));
                if (requestId !== this.#rewardRequestId) return;
                truck.hidden = true;
                truck.style.opacity = '0';
              }
            }
            if (currentStage < 3) {
              await this.#animateTruckTo(track, truck, pct(currentSubtotal), null, fill, pct(progressFrom));
              if (requestId !== this.#rewardRequestId) return;
            }
          } else if (currentStage < 3) {
            await this.#animateTruckTo(track, truck, pct(currentSubtotal), null, fill, pct(previousSubtotal));
            if (requestId !== this.#rewardRequestId) return;
          } else {
            truck.hidden = true;
            truck.style.opacity = '0';
          }
        }
        this.#syncMilestoneBadges(currentSubtotal);
      } else {
        if (currentStage === 3) {
          const completedTruck = this.querySelector('.firstroot-rewards__truck');
          if (completedTruck) {
            completedTruck.hidden = true;
            completedTruck.style.opacity = '0';
          }
          this.querySelector('.firstroot-rewards__milestone--ten')?.classList.remove('is-arriving');
        }
        sessionStorage.setItem('firstroot-reward-total', String(currentTotal));
      }

    } catch (_) {
      // Keep cart updates working if storage or animation APIs are unavailable.
    }
  };
  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}


class FirstRootCartTotal extends HTMLElement {
  static observedAttributes = ['value', 'amount'];
  #display = '';
  #queued = false;
  #animation;
  #generation = 0;
  #face;

  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = ':host{display:inline-block;font:inherit;font-family:var(--font-body--family,Arial,sans-serif)!important;font-weight:700!important;font-variant-numeric:lining-nums tabular-nums;font-feature-settings:"tnum" 1,"lnum" 1;letter-spacing:0!important;color:inherit;white-space:nowrap;-webkit-font-smoothing:antialiased}.face{display:inline-flex;align-items:baseline;line-height:inherit}.currency{font-family:Arial,sans-serif;font-weight:600;flex:none;margin-right:.06em}.digit{display:inline-block;flex:none;width:1ch;min-width:0;perspective:600px;text-align:center;overflow:visible}.rotor{display:inline-grid;width:1ch;transform-style:preserve-3d;transform-origin:50% 50%;will-change:transform}.side{grid-area:1/1;backface-visibility:hidden;-webkit-backface-visibility:hidden}.next{transform:rotateX(180deg)}.separator{display:inline-block;flex:none;width:.28ch;text-align:center;overflow:hidden}';
    this.#face = document.createElement('span');
    this.#face.className = 'face';
    this.#face.setAttribute('aria-hidden', 'true');
    root.append(style, this.#face);
  }

  connectedCallback() {
    this.#generation += 1;
    this.#display = this.getAttribute('value') || this.textContent.trim();
    this.#render(this.#display, this.#display, false);
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#animation?.forEach((animation) => animation.cancel());
    this.#animation = undefined;
  }

  attributeChangedCallback() { this.#queueUpdate(); }

  #queueUpdate() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected && this.#display && !this.#animation) this.#flipToLatest();
    });
  }

  // The resting amount uses exactly the same cells as the rotating amount.
  #render(oldText, text, animate) {
    const pattern = /[\d,]+(?:\.\d+)?/;
    const previous = oldText.match(pattern);
    const next = text.match(pattern);
    if (!previous || !next) {
      this.#face.textContent = text;
      return [];
    }
    const oldDigits = previous[0].replace(/\D/g, '');
    const newDigits = next[0].replace(/\D/g, '');
    const template = oldDigits.length > newDigits.length ? previous[0] : next[0];
    const digitCount = Math.max(oldDigits.length, newDigits.length);
    const fragment = document.createDocumentFragment();
    const motions = [];
    const currency = document.createElement('span');
    currency.className = 'currency';
    currency.textContent = text.slice(0, next.index);
    fragment.append(currency);
    let digitIndex = 0;
    for (const character of template) {
      if (!/\d/.test(character)) {
        const separator = document.createElement('span');
        separator.className = 'separator';
        separator.textContent = character;
        fragment.append(separator);
        // A thousands separator enters/leaves together with its leading digit.
        const offset = digitCount - digitIndex;
        const oldPresent = oldDigits.length > offset;
        const newPresent = newDigits.length > offset;
        if (animate && oldPresent !== newPresent) {
          separator.style.width = newPresent ? '.28ch' : '0ch';
          motions.push({ element: separator, frames: [
            { width: oldPresent ? '.28ch' : '0ch', opacity: oldPresent ? 1 : 0 },
            { width: newPresent ? '.28ch' : '0ch', opacity: newPresent ? 1 : 0 }
          ], delay: 0 });
        }
        continue;
      }
      const offset = digitCount - digitIndex - 1;
      const oldDigit = oldDigits[oldDigits.length - offset - 1] || '';
      const newDigit = newDigits[newDigits.length - offset - 1] || '';
      const cell = document.createElement('span');
      cell.className = 'digit';
      const rotor = document.createElement('span');
      rotor.className = 'rotor';
      const oldFace = document.createElement('span');
      oldFace.className = 'side';
      oldFace.textContent = (animate ? oldDigit : newDigit) || '\u00a0';
      rotor.append(oldFace);
      cell.append(rotor);
      if (animate && oldDigit !== newDigit) {
        const newFace = document.createElement('span');
        newFace.className = 'side next';
        newFace.textContent = newDigit || '\u00a0';
        rotor.append(newFace);
        const rotation = Number(newDigit || 0) >= Number(oldDigit || 0) ? -180 : 180;
        motions.push({ element: rotor, frames: [
          { transform: 'rotateX(0deg)' },
          { transform: 'rotateX(' + rotation + 'deg)' }
        ], delay: Math.min(offset * 24, 96) });
        if (!oldDigit || !newDigit) {
          cell.style.width = newDigit ? '1ch' : '0ch';
          motions.push({ element: cell, frames: [
            { width: oldDigit ? '1ch' : '0ch', opacity: oldDigit ? 1 : 0 },
            { width: newDigit ? '1ch' : '0ch', opacity: newDigit ? 1 : 0 }
          ], delay: 0 });
        }
      }
      fragment.append(cell);
      digitIndex += 1;
    }
    fragment.append(document.createTextNode(text.slice(next.index + next[0].length)));
    this.#face.replaceChildren(fragment);
    return motions;
  }

  #flipToLatest() {
    const text = this.getAttribute('value') || '';
    if (!text || text === this.#display) return;
    if (!this.#face.animate) {
      this.#display = text;
      this.#render(text, text, false);
      return;
    }
    const motions = this.#render(this.#display, text, true);
    const generation = this.#generation;
    const animations = motions.map(({ element, frames, delay }) => element.animate(frames, {
      duration: 600, delay, easing: 'cubic-bezier(.32,0,.2,1)', fill: 'both'
    }));
    this.#animation = animations;
    Promise.all(animations.map((animation) => animation.finished)).then(() => {
      if (generation !== this.#generation) return;
      this.#display = text;
      this.#render(text, text, false);
      animations.forEach((animation) => animation.cancel());
      this.#animation = undefined;
      this.#queueUpdate();
    }).catch(() => {});
  }
}
if (!customElements.get('firstroot-cart-total')) {
  customElements.define('firstroot-cart-total', FirstRootCartTotal);
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}



